import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import config from "../../../../aws-exports";
import {
  FormControl,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormHelperText,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Switch,
  Button,
  IconButton,
  Box,
  Tooltip,
  Autocomplete,
  Paper,
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchFilter from "../../../Requirement/components/common/SearchFilter";
import DataTable from "../../../Requirement/components/common/ReusableTable";
import OfferForm from "./OfferForm";
import { useLoading } from "../../../../context/LoadingContext";
import {
  fetchData,
  postQuery,
  uploadVideoToS3,
  searchfetchQueries,
} from "../../../Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RemoveIcon from "@mui/icons-material/Remove";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ViewOfferForm from "./BankOfferDetails";

const FRfqMatchingOffers = ({
  activeUser,
  userInfo,
  selectedRfq,
  bankList,
}) => {
  const { setLoading } = useLoading();
  const [offerList, setOfferList] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [productDetails, setProductDetails] = useState({});
  const [INRToUsd] = useState(0.012);
  const [searchValues, setSearchValues] = useState({
    searchBank: "",
    searchPriceRange: "",
  });
  const priceOptions = [
    { value: "", label: "Select Range" },
    { value: "0-1000", label: "0-1000" },
    { value: "1000-2000", label: "1000-2000" },
    { value: "2000-5000", label: "2000-5000" },
    { value: "Above-5000", label: "Above 5000" },
  ];

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const loadProductList = useCallback(async () => {
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { Status: "ACTIVE" },
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: { Status: "ACTIVE" },
          }),
        ]);

        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        setProductList(distinctProducts);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    }
  }, []);

  const loadProductGrades = useCallback(async (productName) => {
    try {
      const [allGrades] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 500,
          filterTerms: { ProductName: productName },
        }),
      ]);
      if (allGrades.items.length > 0) {
        setProductGradeList(allGrades.items);
      } else {
        setProductGradeList([]);
      }
    } catch (error) {
      console.error("Error loading product grades: ", error);
    }
  }, []);

  const getProductDetails = useCallback(async (productId) => {
    try {
      const response = await fetchData({
        target: "listProducts",
        userOrder: "ProductID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { ProductID: productId },
      });

      if (response.items && response.items.length > 0) {
        const product = response.items[0];
        setProductDetails({
          ProductName: product.ProductName,
          Grade: product.Grade,
        });
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      setProductDetails({});
    }
  }, []);

  const convertToINR = useCallback(
    (usdValue) => {
      if (!usdValue || usdValue === "") return "0.00";
      const inrValue = parseFloat(usdValue) / INRToUsd;
      return isNaN(inrValue) ? "0.00" : inrValue.toFixed(2);
    },
    [INRToUsd]
  );

  const convertToUSD = useCallback(
    (inrValue) => {
      if (!inrValue || inrValue === "") return "0.00";
      const usdValue = parseFloat(inrValue) * INRToUsd;
      return isNaN(usdValue) ? "0.00" : usdValue.toFixed(2);
    },
    [INRToUsd]
  );

  const formatPrice = useCallback((price) => {
    if (!price || price === "") return "0.00";
    const numPrice = parseFloat(price);
    return isNaN(numPrice) ? "0.00" : numPrice.toFixed(2);
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    try {
      return dayjs(dateString).format("DD-MM-YYYY");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  }, []);

  const fetchMatchingOffers = useCallback(
    async (searchValues, newRowStart = rowStart) => {
      setSelectedOffer(null);
      setLoading(true);
      try {
        let priceFilter = {};
        if (searchValues.searchPriceRange) {
          if (searchValues.searchPriceRange === "Above-5000") {
            priceFilter = { TotalCostGTE: 5000 };
          } else {
            const searchPrice = searchValues.searchPriceRange;
            const [min, max] = searchPrice
              .split("-")
              .map((val) => parseFloat(val));
            priceFilter = {
              TotalCostGTE: min,
              TotalCostLTE: max,
            };
          }
        }

        const body = {
          target: "listFinanceOffers",
          userOrder: "CreatedAt",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            FinanceRfqID: selectedRfq.Id,
            BankID: searchValues.searchBank || "",
            ...priceFilter,
          },
        };

        const response = await fetchData(body);
        if (Array.isArray(response.items) || Array.isArray(response)) {
          const items = response.items || response || [];
          const modifiedItems = (response.items || response).map((item) => ({
            ...item,
            id: item.FinanceRfqOfferID,
          }));
          const itemsWithShortlist = modifiedItems.map((item) => ({
            ...item,
            IsShortlist:
              item.IsShortlist !== undefined ? item.IsShortlist : false,
          }));

          setOfferList(itemsWithShortlist);
          setTotalItems(response.total || 0);
        } else {
          throw new Error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error loading matching offers:", error);
        setOfferList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading, selectedRfq]
  );

  const handleSearch = (searchValues) => {
    setRowStart(0);
    fetchMatchingOffers(searchValues, 0);
  };

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    fetchMatchingOffers(searchValues, newRowStart);
  };

  const handleRowClick = (row, event) => {
    console.log("Offer row clicked:", row);
    console.log("Row structure:", Object.keys(row));
    setSelectedOffer(row);
  };

  const handleOfferResponse = () => {
    console.log("Offer response handled");
    setSelectedOffer(null);
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const fields = [
    {
      name: "searchBank",
      label: "Bank",
      type: "dropdown",
      options: bankList.map((item) => ({
        value: item.BankID,
        label: item.BankName,
      })),
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchBank: value }));
        fetchMatchingOffers({ ...searchValues, searchBank: value }, 0);
      },
      value: searchValues.searchBank,
    },
    {
      name: "searchPriceRange",
      label: "Price Range",
      type: "dropdown",
      options: priceOptions,
      value: searchValues.searchPriceRange,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchPriceRange: value }));
        fetchMatchingOffers({ ...searchValues, searchPriceRange: value }, 0);
      },
    },
  ];

  const columns = [
    { id: "FinanceRfqOfferID", numeric: false, label: "FinanceRfqOfferID" },
    { id: "BankName", numeric: false, label: "Bank Name" },
    { id: "Instrument", numeric: false, label: "Instrument" },
    { id: "Currency", numeric: false, label: "Currency" },
    { id: "TotalCost", numeric: false, label: "Total Cost" },
    { id: "Status", numeric: false, label: "Status" },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created Date",
      renderCell: (row) => formatDate(row.CreatedAt),
    },
  ];

  useEffect(() => {
    fetchMatchingOffers(searchValues);
  }, [rowStart, pageSize, fetchMatchingOffers, searchValues, selectedRfq]);

  return (
    <div>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Bank Offers</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={offerList}
        total={totalItems}
        title="Matching Offers"
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        currentPage={currentPage}
        onRowClick={handleRowClick}
        selectedRows={
          selectedOffer
            ? [selectedOffer.FinanceRfqOfferID || selectedOffer.id]
            : []
        }
      />

      {selectedOffer && (
        <Box sx={{ mt: 4 }}>
          <ViewOfferForm
            rfqNo={selectedRfq?.RfqNo || ""}
            offerData={selectedOffer}
          />
        </Box>
      )}
    </div>
  );
};

export default FRfqMatchingOffers;
